module Main exposing (main)

import Browser
import Browser.Events
import Html exposing (Html)
import Html.Attributes
import Json.Decode as Decode
import Math.Vector2 as Vec2
import Task
import Time
import WebGL
import WebGL.Texture as Texture exposing (Texture)
import Billiard.Model as Model
import Billiard.Render as Render


type alias Flags =
    { windowWidth : Int
    , windowHeight : Int
    , textureUrls : List String  -- Kugel-UV-Maps, index = Kugelnummer
    }


main : Program Flags AppModel Msg
main =
    Browser.document
        { init = init
        , view = view
        , update = update
        , subscriptions = subscriptions
        }



-- MODEL


type alias AppModel =
    { billiard : Model.Model
    }


type Msg
    = WindowResized Int Int
    | TimeTick Time.Posix  -- aktuelle Zeit (von Time.every)
    | AimStart Float Float  -- Maus-Down (Canvas-Pixel)
    | AimMove Float Float
    | AimEnd Float Float
    | TextureLoaded Int (Result Texture.Error Texture)  -- Kugeltextur geladen



-- INIT


init : Flags -> ( AppModel, Cmd Msg )
init flags =
    let
        billiard = Model.init flags.textureUrls flags.windowWidth flags.windowHeight
        -- Alle Kugeltexturen asynchron laden (index = Kugelnummer); bis dahin
        -- rendert das Rendering die flache Rückfall-Farbe.
        loadCommands =
            List.indexedMap
                (\i url -> Task.attempt (TextureLoaded i) (Model.loadTexture url))
                flags.textureUrls
    in
    ( { billiard = billiard }, Cmd.batch loadCommands )



-- UPDATE


update : Msg -> AppModel -> ( AppModel, Cmd Msg )
update msg model =
    case msg of
        WindowResized w h ->
            let
                billiard = model.billiard
                updated = { billiard | windowWidth = toFloat w, windowHeight = toFloat h }
            in
            ( { model | billiard = updated }, Cmd.none )

        TimeTick posix ->
            let
                nowMs = toFloat (Time.posixToMillis posix)
            in
            ( { model | billiard = Model.advance model.billiard nowMs }, Cmd.none )

        AimStart px py ->
            -- Anvisen nur, wenn die Tabelle steht
            if Model.tableAtRest model.billiard then
                let
                    billiard = model.billiard
                    wp = Model.screenToWorld billiard px py
                    newBilliard = { billiard | aim = Just { start = wp, current = wp } }
                in
                ( { model | billiard = newBilliard }, Cmd.none )
            else
                ( model, Cmd.none )

        AimMove px py ->
            case model.billiard.aim of
                Just aim ->
                    let
                        billiard = model.billiard
                        wp = Model.screenToWorld billiard px py
                        newBilliard = { billiard | aim = Just { aim | current = wp } }
                    in
                    ( { model | billiard = newBilliard }, Cmd.none )
                Nothing ->
                    ( model, Cmd.none )

        AimEnd _ _ ->
            case model.billiard.aim of
                Just aim ->
                    let
                        -- Slingshot: Schussrichtung = start − current (Rückzug)
                        drag = Vec2.sub aim.start aim.current
                        dragLength = Vec2.length drag
                        maxSpeed = model.billiard.config.maxShotSpeed
                        shotSpeed = min (dragLength * shotPowerScale) maxSpeed

                        newBalls =
                            if shotSpeed > 0.05 then
                                List.map
                                    (\b ->
                                        if b.number == 0 then
                                            { b | velocity = Vec2.scale (shotSpeed / dragLength) drag }
                                        else
                                            b
                                    )
                                    model.billiard.balls
                            else
                                model.billiard.balls
                    in
                    let
                        billiard = model.billiard
                        newBilliard = { billiard | aim = Nothing, balls = newBalls }
                    in
                    ( { model | billiard = newBilliard }, Cmd.none )
                Nothing ->
                    ( model, Cmd.none )

        TextureLoaded index result ->
            let
                billiard = model.billiard
                textures =
                    case result of
                        Ok texture ->
                            List.indexedMap
                                (\i t ->
                                    if i == index then
                                        Just texture
                                    else
                                        t
                                )
                                billiard.textures
                        Err _ ->
                            billiard.textures  -- Fallback-Farbe bleibt aktiv
            in
            ( { model | billiard = { billiard | textures = textures } }, Cmd.none )



-- Ziehlänge (m) → Schussgeschwindigkeit (m/s)
shotPowerScale : Float
shotPowerScale =
    5


-- DECODER für Maus-Events: liefert (clientX, clientY) in Canvas-Pixel
mousePosDecoder : ( Float -> Float -> Msg ) -> Decode.Decoder Msg
mousePosDecoder msg =
    Decode.map2
        msg
        (Decode.field "clientX" Decode.float)
        (Decode.field "clientY" Decode.float)


-- VIEW


view : AppModel -> Browser.Document Msg
view model =
    { title = "Billard-Simulation"
    , body = [ renderScene model.billiard ]
    }


renderScene : Model.Model -> Html Msg
renderScene billiard =
    let
        w = floor billiard.windowWidth
        h = floor billiard.windowHeight
    in
    WebGL.toHtml
        [ Html.Attributes.width w
        , Html.Attributes.height h
        , Html.Attributes.style "display" "block"
        , Html.Attributes.style "position" "fixed"
        , Html.Attributes.style "top" "0"
        , Html.Attributes.style "left" "0"
        , Html.Attributes.style "width" "100vw"
        , Html.Attributes.style "height" "100vh"
        ]
        (Render.render billiard)



-- SUBSCRIPTIONS


subscriptions : AppModel -> Sub Msg
subscriptions _ =
    Sub.batch
        [ Browser.Events.onResize WindowResized
        , Time.every 16 TimeTick
        -- Canvas ist fullscreen (position: fixed), daher clientX/Y = Canvas-Pixel
        , Browser.Events.onMouseDown (mousePosDecoder AimStart)
        , Browser.Events.onMouseMove (mousePosDecoder AimMove)
        , Browser.Events.onMouseUp (mousePosDecoder AimEnd)
        ]
